import { css } from 'emotion';
import React, { useEffect, useState } from 'react';
import Icon from 'shared/Icon';
import { UploadErrorIcon } from 'shared/Icon/components/UploadErrorIcon';
import { events } from 'screens/EpisodeScreen/events';
import { WaveformAudioInfo } from 'screens/EpisodeScreen/context';
import { DURATION_ERROR_MESSAGE } from 'screens/EpisodeScreen/components/CuePoints/constants';
import { useCuePoints } from 'screens/EpisodeScreen/components/CuePoints/useCuePoints';
import { SetPodcastEpisodeId } from 'screens/EpisodeScreen/components/Form/types';
import { SAIWaveformModal } from 'screens/EpisodeScreen/components/AdInsertion/WaveformModal';
import { AdsLoadingErrorBanner } from 'components/AdInsertion/components/AdsLoadingErrorBanner';
import { StyledButton } from 'screens/EpisodeScreen/components/MediaUploader/styles';
import {
  Container,
  ContentContainer,
  IconWrapper,
  ModuleHeader,
  Info,
} from 'components/AdInsertion/styles';
import { AdInsertionContext } from 'components/AdInsertion/context';
import { Button } from 'client/shared/Button/NewButton';
import { IconBadge } from 'client/components/IconBadge';
import { AdInsertionErrorModal } from 'components/AdInsertion/components/AdInsertionErrorModal';
import { AdInsertionUIType } from 'components/AdInsertion/types';
import { useFetchEpisodeOverview } from 'client/hooks/episodes/useFetchEpisodeOverview';
import {
  AdInsertionPaywallDisabledAdsWarning,
  ENABLE_PAYWALLS_DISABLED_ADS_WARNING,
} from './components/AdInsertionPaywallDisabledAdsWarning';

type AdInsertionProps = {
  podcastEpisodeId?: string;
  duration: number;
  waveformAudioInfo: WaveformAudioInfo;
  setPodcastEpisodeId: SetPodcastEpisodeId;
  isWaveformEnabled?: boolean;
  kind?: AdInsertionUIType;
  isDisabled?: boolean;
  isErrorFetchEpisodeAudio?: boolean;
  refetch?: () => void;
  isPWEpisode?: boolean;
};

export function AdInsertion({
  duration,
  waveformAudioInfo,
  podcastEpisodeId,
  setPodcastEpisodeId,
  isWaveformEnabled = true,
  kind = 'section',
  isDisabled = false,
  isErrorFetchEpisodeAudio = false,
  refetch,
  isPWEpisode,
}: AdInsertionProps) {
  const [showModal, setShowModal] = useState(false);
  const [cuePointCount, setCuePointCount] = useState<number | null>(null);
  const [hasInvalidCuePointError, setHasInvalidCuePointError] = useState(false);
  const showErrorBanner = refetch && isErrorFetchEpisodeAudio;
  const { episodeOverviewData } = useFetchEpisodeOverview({
    webEpisodeId: podcastEpisodeId!,
  });
  const {
    state: { newCuePoint, status, errors, savedCuePoints, errorModal },
    updateNewCuePoint,
    addCuePoint,
    removeCuePoint,
    editCuePoint,
    dismissErrorModal,
    clearErrors,
  } = useCuePoints({
    initialPodcastEpisodeId: podcastEpisodeId,
    setPodcastEpisodeId,
    mediaDuration: duration,
  });
  const hasAdCuePoints = savedCuePoints ? savedCuePoints.length > 0 : false;
  const { episodeAudios = [] } = episodeOverviewData || {};
  const hasAdSegments = episodeAudios.some(episode => episode.type === 'ad');

  useEffect(() => {
    if (savedCuePoints) {
      setCuePointCount(
        savedCuePoints.reduce((acc, curr) => acc + curr.adCount, 0)
      );
    }
  }, [savedCuePoints]);

  useEffect(() => {
    if (errors && errors.find(err => err.message === DURATION_ERROR_MESSAGE)) {
      setHasInvalidCuePointError(true);
    } else {
      setHasInvalidCuePointError(false);
    }
  }, [errors]);

  useEffect(() => {
    // file has been removed most likely
    if (duration === 0) {
      setHasInvalidCuePointError(false);
    }
  }, [duration]);

  const onButtonClick = () => {
    events.trackInsertAdButtonClick();
    setShowModal(true);
  };

  const onClickClose = () => {
    setShowModal(false);
  };

  const hasCuePoints = !!cuePointCount && cuePointCount > 0;

  const fillColor = hasInvalidCuePointError
    ? '#ffffff'
    : hasCuePoints
    ? '#70EA65'
    : '#dedfe0';

  const bodyCopy = hasCuePoints
    ? 'Edit, add, or remove your ad slots.'
    : 'Insert your ad slots to monetize your podcast.';

  /**
   * different UIs to render for ad insertion, depending on the `kind` prop passed
   */
  function renderAdInsertionUI() {
    switch (kind) {
      case 'addAdSegmentButton':
        // this is the button that would live in the episode staging area
        return (
          <>
            {showErrorBanner && (
              <AdsLoadingErrorBanner onClickReload={refetch} />
            )}
            <Button
              className={css`
                width: 100%;
                margin-top: 8px;
                border: 1px solid #dfe0e1;
              `}
              onClick={onButtonClick}
              isDisabled={isDisabled}
            >
              <div
                className={css`
                  margin-right: 12px;
                `}
              >
                <IconBadge
                  backgroundColor="green"
                  padding={3}
                  type="DollarSignIcon"
                  ariaHidden={true}
                />
              </div>
              Edit ad locations ({cuePointCount})
            </Button>
          </>
        );

      // TODO: update this button name to be more descriptive, less generic
      // https://anchorfm.atlassian.net/browse/PODRACER-1965
      case 'button':
        // this is a standalone button that can either open the waveform or link to the episode builder
        return (
          <>
            <div
              className={css`
                display: flex;
              `}
            >
              {showErrorBanner && (
                <AdsLoadingErrorBanner onClickReload={refetch} />
              )}
              <Button
                color="white"
                onClick={isWaveformEnabled ? onButtonClick : () => null}
                kind={isWaveformEnabled ? 'button' : 'link'}
                href={
                  isWaveformEnabled
                    ? ''
                    : `/dashboard/episode/${podcastEpisodeId}/edit`
                }
                isDisabled={isDisabled}
              >
                <div
                  className={css`
                    margin-right: 12px;
                  `}
                >
                  <IconBadge
                    iconColor={isDisabled ? '#CCCDCF' : '#fff'}
                    backgroundColor={isDisabled ? 'lightGray' : 'green'}
                    padding={3}
                    type="DollarSignIcon"
                    ariaHidden={true}
                  />
                </div>
                Edit ad locations{' '}
                {isWaveformEnabled && hasCuePoints && `(${cuePointCount})`}
              </Button>
            </div>
          </>
        );
      case 'section':
        // this is a page-wide section for ad insertion
        return (
          <div>
            <ModuleHeader>Ad insertion</ModuleHeader>
            <Container opacity={1}>
              <ContentContainer>
                <IconWrapper fillColor={fillColor}>
                  {hasInvalidCuePointError ? (
                    <UploadErrorIcon size={{ width: 64, height: 64 }} />
                  ) : (
                    <Icon type="dollar_sign" fillColor="#ffffff" />
                  )}
                </IconWrapper>
                <Info hasError={hasInvalidCuePointError}>
                  {hasInvalidCuePointError ? (
                    <>
                      <h3>One or more of your ad slots has been disabled.</h3>
                      <h3>Edit ads to resolve errors.</h3>
                    </>
                  ) : (
                    <>
                      <p>{bodyCopy}</p>
                    </>
                  )}
                </Info>

                {isWaveformEnabled ? (
                  <div
                    className={css`
                      display: flex;
                    `}
                  >
                    {showErrorBanner && (
                      <AdsLoadingErrorBanner onClickReload={refetch} />
                    )}
                    <StyledButton
                      className={css`
                        width: 175px;
                        color: black;
                      `}
                      onClick={onButtonClick}
                      color="white"
                      isDisabled={isDisabled}
                    >
                      {!cuePointCount && !hasInvalidCuePointError
                        ? 'Insert ads'
                        : `Edit ads (${cuePointCount})`}
                    </StyledButton>
                  </div>
                ) : (
                  <>
                    {showErrorBanner && (
                      <AdsLoadingErrorBanner onClickReload={refetch} />
                    )}
                    <StyledButton
                      kind="link"
                      className={css`
                        width: 175px;
                      `}
                      href={`/dashboard/episode/${podcastEpisodeId}/edit`}
                      color="white"
                      isDisabled={isDisabled}
                    >
                      Insert ads
                    </StyledButton>
                  </>
                )}
              </ContentContainer>
              {ENABLE_PAYWALLS_DISABLED_ADS_WARNING && (
                <AdInsertionPaywallDisabledAdsWarning
                  hasAdCuePoints={hasAdCuePoints}
                  hasAdSegments={hasAdSegments}
                  isPWEpisode={isPWEpisode}
                />
              )}
            </Container>
          </div>
        );
      default:
        return null;
    }
  }
  return (
    <AdInsertionContext.Provider
      value={{
        setCuePointCount,
        setHasInvalidCuePointError,
        cuePointsState: { newCuePoint, status, errors, savedCuePoints },
        updateNewCuePoint,
        addCuePoint,
        removeCuePoint,
        editCuePoint,
        clearErrors,
      }}
    >
      {renderAdInsertionUI()}
      {showModal && (
        <SAIWaveformModal
          duration={duration}
          onClickClose={onClickClose}
          waveformAudioInfo={waveformAudioInfo}
        />
      )}
      {/**
       * only show error modal when the AdInsertion is on the episode builder or
       * episode options page
       */}
      {errorModal && ['addAdSegmentButton', 'section'].includes(kind) && (
        <AdInsertionErrorModal
          errorModal={errorModal}
          dismissErrorModal={dismissErrorModal}
        />
      )}
    </AdInsertionContext.Provider>
  );
}
